// The frame `survey.mount(element)` draws an embedded survey in. The page
// hands the snippet an element; the snippet fills it with an iframe of the
// widget's `/survey-card` route, which draws Weloop's own survey card – the
// card the dashboard previews – in the project's colors. The host page's CSS
// never reaches inside, so the survey looks the same on every site.
//
// The frame only draws. Answers go back through the survey handle the page
// received, so impressions, cooldowns and validation stay in one place:
//
//   frame → page   weloop:survey-card-ready     {}                  listening
//   page → frame   weloop:survey-card-show      { survey, appearance }
//   frame → page   weloop:survey-card-resize    { height }
//   frame → page   weloop:survey-card-answer    { requestId, score, comment?, isPartial? }
//                                               or { requestId, answers }
//   page → frame   weloop:survey-card-result    { requestId, ok }
//   frame → page   weloop:survey-card-draft     { answers }         saved as typed
//   frame → page   weloop:survey-card-dismiss   {}                  closed unanswered
//   frame → page   weloop:survey-card-done      {}                  thanks shown
//
// Both ends import these names, so the snippet and the widget cannot drift.

/** The widget route the frame loads. */
export const SURVEY_CARD_FRAME_PATH = "/survey-card";

export const SURVEY_CARD_MESSAGE = {
	answer: "weloop:survey-card-answer",
	dismiss: "weloop:survey-card-dismiss",
	done: "weloop:survey-card-done",
	draft: "weloop:survey-card-draft",
	ready: "weloop:survey-card-ready",
	resize: "weloop:survey-card-resize",
	result: "weloop:survey-card-result",
	show: "weloop:survey-card-show",
} as const;

/**
 * How the project dresses its survey card: the widget settings' primary
 * color, and the launcher mark the thank-you card celebrates with. The
 * widget sends it with the survey; page code never sees it.
 */
export interface SurveyCardAppearance {
	readonly brandMark: {
		readonly emoji: string;
		readonly imageUrl: string | null;
	};
	/** The frame's interface language, so its buttons match the widget's. */
	readonly locale: string;
	/** Hex color from the project's widget settings. */
	readonly primaryColor: string;
}

// Same rule as the widget settings' `HexColorSchema`, without pulling Zod
// into the snippet bundle.
const HEX_COLOR = /^#[0-9a-f]{6}$/iu;

const isObject = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

/**
 * Narrows an appearance off the wire. Anything malformed yields `undefined`
 * and the caller falls back to Weloop's default look.
 */
export function parseSurveyCardAppearance(
	raw: unknown,
): SurveyCardAppearance | undefined {
	if (!isObject(raw) || !isObject(raw["brandMark"])) {
		return undefined;
	}
	const { brandMark, locale, primaryColor } = raw;
	const { emoji, imageUrl } = brandMark;
	if (
		typeof locale !== "string" ||
		typeof primaryColor !== "string" ||
		!HEX_COLOR.test(primaryColor) ||
		typeof emoji !== "string" ||
		(imageUrl !== null && typeof imageUrl !== "string")
	) {
		return undefined;
	}
	return { brandMark: { emoji, imageUrl }, locale, primaryColor };
}
